import { useEffect, useState } from 'react';
import {
  Modal,
  Stack,
  Group,
  Text,
  TextInput,
  Button,
  Loader,
  Center,
  Alert,
  Radio,
  Card,
  UnstyledButton,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { AlertCircle, Search, ChevronRight } from 'react-feather';
import classes from './DuplicateFromSessionModal.module.css';
import { cohortsApi } from '../../../../../services/api/cohorts';
import {
  sessionAgendaApi,
  type SessionAgendaDuplicateCandidate,
  type SessionAgendaDuplicateFromResponse,
} from '../../../../../services/api/sessionAgenda';
import type { CohortWithDetails } from '../../../../../types';

type Step = 'cohort' | 'session' | 'confirm';

/**
 * MED-1110 — the copy target is addressed one of two ways, matching the editor's
 * two entry points (MED-1086 / E1.3m):
 *   - `session`: a concrete sessionId (groups exist) — copies agenda content AND
 *     the source slide deck.
 *   - `byNumber`: a (cohort, session_number) scope with no groups yet — copies
 *     agenda content only (no deck; the deck slot is hidden pre-groups).
 */
export type DuplicateFromTarget =
  | { mode: 'session'; sessionId: string }
  | { mode: 'byNumber'; sessionNumber: number };

export interface DuplicateFromSessionModalProps {
  opened: boolean;
  onClose: () => void;
  /** The cohort owning the target session. Used as the cohortId path param on the duplicate-from POST. */
  targetCohortId: string;
  /** The copy target — a concrete session (deck copied) or a session number (agenda only). */
  target: DuplicateFromTarget;
  /** Called after a successful duplicate-from response; the editor pours `description`/`sections`/`participant_pre_session_work` into its draft state and refreshes its content-files list to surface the new slide deck. */
  onConfirmed: (payload: SessionAgendaDuplicateFromResponse) => void;
}

interface PickedCohort {
  id: string;
  name: string;
  partner_name: string;
  program_name: string;
}

/**
 * MED-982 (E1.3g) — Two-step picker for the "Start from an existing session"
 * flow.
 *
 * Step 1 ("cohort"): cohort typeahead — reuses the existing
 * `GET /api/cohorts?search=...` endpoint, which is already scoped to cohorts
 * the user can read.
 *
 * Step 2 ("session"): per-cohort source-session radio list — fetched from
 * `GET /api/cohorts/:cohortId/sessions/duplicate-candidates`. Sessions
 * without authored content come back with `has_authored_content === false`
 * and render as disabled radio rows with a tooltip hint (AC-EP3).
 *
 * Step 3 ("confirm"): summary card + a paragraph explaining what gets copied
 * (and that the session name/number does NOT) before firing the POST. AC-EP4.
 *
 * The modal does NOT handle the "target already has content" overwrite
 * confirmation — that is upstream in `SessionContentEditorPage` (AC-CB4),
 * because only the editor knows whether the current draft has content. The
 * editor opens this modal only after the user has confirmed they are okay
 * overwriting.
 */
export function DuplicateFromSessionModal(props: DuplicateFromSessionModalProps) {
  const { opened, onClose, targetCohortId, target, onConfirmed } = props;

  // -- Cohort step state ---------------------------------------------------
  const [step, setStep] = useState<Step>('cohort');
  const [cohortQuery, setCohortQuery] = useState('');
  const [debouncedQuery] = useDebouncedValue(cohortQuery, 250);
  const [cohorts, setCohorts] = useState<CohortWithDetails[]>([]);
  const [loadingCohorts, setLoadingCohorts] = useState(false);
  const [pickedCohort, setPickedCohort] = useState<PickedCohort | null>(null);

  // -- Session step state --------------------------------------------------
  const [candidates, setCandidates] = useState<SessionAgendaDuplicateCandidate[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [candidatesError, setCandidatesError] = useState<string | null>(null);
  const [pickedCandidate, setPickedCandidate] =
    useState<SessionAgendaDuplicateCandidate | null>(null);

  // -- Confirm-step state --------------------------------------------------
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reset everything when the modal closes — important so a re-open of the
  // modal doesn't preserve the prior selection / step.
  useEffect(() => {
    if (opened) return;
    setStep('cohort');
    setCohortQuery('');
    setCohorts([]);
    setPickedCohort(null);
    setCandidates([]);
    setCandidatesError(null);
    setPickedCandidate(null);
    setSubmitError(null);
    setSubmitting(false);
  }, [opened]);

  // Typeahead cohort fetch. Fires on every debounced query change while we're
  // on the cohort step. Errors are swallowed — the UI just shows the empty
  // state, which is the same UX as a literal zero-match search.
  useEffect(() => {
    if (!opened || step !== 'cohort') return;
    let cancelled = false;
    setLoadingCohorts(true);
    cohortsApi
      .list({
        search: debouncedQuery || undefined,
        limit: 20,
        sortBy: 'created_at',
        sortOrder: 'DESC',
      })
      .then(res => {
        if (cancelled) return;
        setCohorts(res.data);
      })
      .catch(() => {
        if (cancelled) return;
        setCohorts([]);
      })
      .finally(() => {
        if (cancelled) return;
        setLoadingCohorts(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, opened, step]);

  // Fetch session candidates when a cohort is picked. The picker greys out
  // candidates with has_authored_content=false; we leave them in the list
  // (instead of filtering them out) so the CM can see the full session
  // structure of the cohort and understand why a session can't be picked.
  const handlePickCohort = (cohort: CohortWithDetails) => {
    setPickedCohort({
      id: cohort.id,
      name: cohort.name,
      partner_name: cohort.partner_name,
      program_name: cohort.program_name,
    });
    setStep('session');
    setLoadingCandidates(true);
    setCandidatesError(null);
    sessionAgendaApi
      .listDuplicateCandidates(cohort.id)
      .then(setCandidates)
      .catch(err => {
        setCandidatesError(err instanceof Error ? err.message : 'Could not load sessions.');
        setCandidates([]);
      })
      .finally(() => setLoadingCandidates(false));
  };

  const handleConfirm = async () => {
    if (!pickedCandidate) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload =
        target.mode === 'session'
          ? await sessionAgendaApi.duplicateFrom(
              targetCohortId,
              target.sessionId,
              pickedCandidate.session_id,
            )
          : await sessionAgendaApi.duplicateFromByNumber(
              targetCohortId,
              target.sessionNumber,
              pickedCandidate.session_id,
            );
      onConfirmed(payload);
      onClose();
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : 'Could not start from this session. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Start from an existing session"
      centered
      size="lg"
      closeOnClickOutside={!submitting}
      closeOnEscape={!submitting}
    >
      {step === 'cohort' && (
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Step 1 of 2 — Choose the cohort whose session you want to copy from.
          </Text>
          <TextInput
            label="Search cohorts"
            placeholder="Type to search by cohort name..."
            value={cohortQuery}
            onChange={e => setCohortQuery(e.currentTarget.value)}
            leftSection={<Search size={14} />}
            autoFocus
            data-testid="duplicate-from-cohort-search"
          />
          {loadingCohorts ? (
            <Center mih={220}>
              <Loader />
            </Center>
          ) : cohorts.length === 0 ? (
            <Center mih={220}>
              <Text c="dimmed" size="sm">
                {debouncedQuery
                  ? 'No cohorts match your search.'
                  : 'No cohorts available.'}
              </Text>
            </Center>
          ) : (
            <Stack gap="xs" mah={320} className={classes.scrollList}>
              {cohorts.map(c => (
                <UnstyledButton
                  key={c.id}
                  className={classes.cohortRow}
                  onClick={() => handlePickCohort(c)}
                  data-testid={`duplicate-from-cohort-row-${c.id}`}
                >
                  <Group justify="space-between" align="center" wrap="nowrap">
                    <Stack gap={2}>
                      <Text fw={500}>{c.name}</Text>
                      <Text size="xs" c="dimmed">
                        {c.partner_name} · {c.program_name}
                      </Text>
                    </Stack>
                    <ChevronRight size={16} />
                  </Group>
                </UnstyledButton>
              ))}
            </Stack>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
          </Group>
        </Stack>
      )}

      {step === 'session' && pickedCohort && (
        <Stack gap="md">
          <Group justify="space-between" align="center">
            <Stack gap={2}>
              <Text size="sm" c="dimmed">
                Step 2 of 2 — Choose a session from{' '}
                <Text span fw={500} c="inherit">
                  {pickedCohort.name}
                </Text>
              </Text>
              <Text size="xs" c="dimmed">
                {pickedCohort.partner_name} · {pickedCohort.program_name}
              </Text>
            </Stack>
            <Button variant="subtle" size="xs" onClick={() => setStep('cohort')}>
              Change cohort
            </Button>
          </Group>

          {loadingCandidates ? (
            <Center mih={220}>
              <Loader />
            </Center>
          ) : candidatesError ? (
            <Alert color="dangerRed" icon={<AlertCircle size={16} />} title="Could not load sessions">
              {candidatesError}
            </Alert>
          ) : candidates.length === 0 ? (
            <Alert color="warningAmber" icon={<AlertCircle size={16} />}>
              This cohort has no group coaching sessions to copy from.
            </Alert>
          ) : (
            <Radio.Group
              value={pickedCandidate?.session_id ?? null}
              onChange={value =>
                setPickedCandidate(candidates.find(c => c.session_id === value) ?? null)
              }
            >
              <Stack gap="xs" mah={320} className={classes.scrollList}>
                {candidates.map(c => (
                  <Tooltip
                    key={c.session_id}
                    label="This session has no authored content yet — there's nothing to copy."
                    disabled={c.has_authored_content}
                    multiline
                    w={260}
                    position="top-start"
                  >
                    <div>
                      <Radio
                        value={c.session_id}
                        disabled={!c.has_authored_content}
                        label={
                          <Stack gap={2}>
                            <Text
                              fw={500}
                              c={c.has_authored_content ? undefined : 'dimmed'}
                              data-testid={`duplicate-from-candidate-title-${c.session_number}`}
                            >
                              Session {c.session_number} — {c.title}
                            </Text>
                            {!c.has_authored_content && (
                              <Text size="xs" c="dimmed">
                                No authored content yet
                              </Text>
                            )}
                          </Stack>
                        }
                      />
                    </div>
                  </Tooltip>
                ))}
              </Stack>
            </Radio.Group>
          )}

          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button
              color="medleyGreen"
              onClick={() => setStep('confirm')}
              disabled={!pickedCandidate}
            >
              Continue
            </Button>
          </Group>
        </Stack>
      )}

      {step === 'confirm' && pickedCohort && pickedCandidate && (
        <Stack gap="md">
          <Text size="sm">You&apos;re about to copy the authored content from:</Text>
          <Card withBorder padding="md" radius="md">
            <Stack gap={4}>
              <Text size="xs" c="dimmed">
                {pickedCohort.name} · {pickedCohort.partner_name} · {pickedCohort.program_name}
              </Text>
              <Text fw={500}>
                Session {pickedCandidate.session_number} — {pickedCandidate.title}
              </Text>
            </Stack>
          </Card>
          <Text size="sm" c="dimmed">
            The session&apos;s description, agenda sections (including leader prompts
            {target.mode === 'session' ? ' and slide associations' : ''}),
            {target.mode === 'session' ? ' slide deck (if any),' : ''} and participant
            pre-session work will be loaded into this editor. The current session&apos;s
            name and number stay the same. Nothing is saved until you Save in the editor.
          </Text>
          {submitError && (
            <Alert color="dangerRed" icon={<AlertCircle size={16} />}>
              {submitError}
            </Alert>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setStep('session')} disabled={submitting}>
              Back
            </Button>
            <Button
              color="medleyGreen"
              onClick={() => void handleConfirm()}
              loading={submitting}
              data-testid="duplicate-from-confirm-button"
            >
              Copy content
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
